import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ArrowRight, Clock, FerrisWheel, Package, Plus, Ticket, Wand2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StepIndicator } from '@/components/pos/StepIndicator';
import { MoneyValue } from '@/components/pos/MoneyValue';
import {
  cartSelectors,
  selectTotal,
  useCartStore,
  type CartLine,
  type CartLineAddOn,
} from '@/stores/cart.store';
import { useShallow } from 'zustand/react/shallow';
import { useProducts, useAvailability } from '@/hooks/queries';
import { usePriceSync } from '@/hooks/usePriceSync';
import { useHotkeys } from '@/hooks/useHotkeys';
import { toast } from 'sonner';
import { isAttraction } from '@/lib/products';
import { formatTime } from '@/lib/format';
import { SLOT_NONE, selectableSlots, slotTimeLabel } from '@/lib/slots';
import { SmartItineraryDialog } from '@/components/pos/SmartItinerary/SmartItineraryDialog';
import {
  CAP_REACHED_LABEL,
  buildCartCounts,
  capOfProduct,
  capReachedSlotIds,
  findCapViolations,
  isCapped,
} from '@/lib/purchaseCap';
import type { Product } from '@/types/product';
import type { Slot } from '@/types/product.slot';
import type { ItineraryConfig, ItineraryPlan, SchedulableItem } from '@/types/itinerary';

/** A slot-carrying item on a prospective wristband (main seat, add-on, component). */
interface WristbandItem {
  productId: string;
  slotId?: string;
  quantity?: number;
}

/**
 * Per-(attraction, slot) counts for ONE prospective wristband — this is a FRESH
 * sale, so a seat/bundle IS a new wristband and `held` is always empty; only
 * these cart counts feed the purchase-cap gate. The quota is per ATTRACTION, so
 * items sharing an `attractionId` on the same wristband are folded together.
 */
function buildWristbandCart(
  items: WristbandItem[],
  productById: Map<string, Product>,
): Map<string, number> {
  return buildCartCounts(
    items.map((it) => {
      const n = Math.max(1, it.quantity ?? 1);
      return {
        attractionId: productById.get(it.productId)?.attractionId,
        slotIds: it.slotId ? Array.from({ length: n }, () => it.slotId as string) : [],
      };
    }),
  );
}

/** Whether this wristband's current selection breaches any per-slot cap. */
function wristbandHasCapViolation(
  items: WristbandItem[],
  productById: Map<string, Product>,
): boolean {
  return (
    findCapViolations({
      lines: items.map((it) => {
        const p = productById.get(it.productId);
        const n = Math.max(1, it.quantity ?? 1);
        return {
          attractionId: p?.attractionId,
          attractionName: p?.name ?? null,
          cap: capOfProduct(p),
          slotIds: it.slotId ? Array.from({ length: n }, () => it.slotId as string) : [],
        };
      }),
    }).length > 0
  );
}

/**
 * One attraction (child) ticket under a visitor — its own name + time slot +
 * remove. Each attraction ticket carries an independent slot.
 */
function AttractionChildRow({
  child,
  attraction,
  visitDate,
  cart,
  onSlot,
  onRemove,
}: {
  child: CartLineAddOn;
  attraction?: Product;
  visitDate: string;
  /** Per-(attraction, slot) counts for this child's wristband — cap gate input. */
  cart: Map<string, number>;
  onSlot: (slot: Slot | undefined) => void;
  onRemove: () => void;
}) {
  const needsSlot = Boolean(attraction?.requiresSlot);
  const slotQuery = useAvailability(child.productId, visitDate, needsSlot);
  const slots = useMemo(() => selectableSlots(slotQuery.data?.slots ?? []), [slotQuery.data]);
  const missing = needsSlot && !child.slotId;

  // Purchase-cap gate (UX only; the /sale path re-checks and 409s). Absent/-1 → inert.
  const cap = capOfProduct(attraction);
  const blockedSlotIds = isCapped(cap)
    ? capReachedSlotIds({ cap, attractionId: attraction?.attractionId, slots, cart, held: null })
    : null;

  // Only one slot available → auto-select it (no reason to make the cashier pick).
  useEffect(() => {
    if (needsSlot && slots.length === 1 && !child.slotId) onSlot(slots[0]);
  }, [needsSlot, slots, child.slotId, onSlot]);

  return (
    <div className="rounded-md border-2 border-border bg-card p-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <FerrisWheel className="h-3.5 w-3.5 shrink-0 text-gold-600" />
          <span className="truncate text-sm font-medium text-ink-900">{child.name}</span>
          <MoneyValue value={child.price} className="text-xs text-ink-400" />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-ink-400 hover:text-destructive"
          onClick={onRemove}
          aria-label={`Hapus ${child.name}`}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      {needsSlot && (
        <div className="mt-2">
          {slotQuery.isLoading ? (
            <Skeleton className="h-8 w-full" />
          ) : slots.length === 0 ? (
            <p className="text-xs text-warning">Tidak ada slot untuk tanggal ini.</p>
          ) : (
            <Select
              value={child.slotId ?? SLOT_NONE}
              onValueChange={(v) => onSlot(v === SLOT_NONE ? undefined : slots.find((s) => s.id === v))}
            >
              <SelectTrigger className="h-8" aria-label={`Waktu ${child.name}`}>
                <SelectValue placeholder="Pilih waktu" />
              </SelectTrigger>
              <SelectContent>
                {slots.map((s) => {
                  // Cap-blocked unless it's this unit's own current pick.
                  const capBlocked = !!blockedSlotIds?.has(s.id) && s.id !== child.slotId;
                  return (
                    <SelectItem key={s.id} value={s.id} disabled={capBlocked}>
                      {slotTimeLabel(s)}
                      {capBlocked ? ` · ${CAP_REACHED_LABEL}` : ` · Sisa ${s.availableCount}`}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
          {missing && slots.length > 0 && (
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-warning">
              <AlertCircle className="h-3 w-3" /> Pilih waktu
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** "Tambah wahana" picker — closes as soon as an attraction is chosen. */
function AddAttractionPopover({
  attractions,
  onSelect,
}: {
  attractions: Product[];
  onSelect: (product: Product) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 w-full gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Tambah wahana
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        <div className="max-h-64 space-y-0.5 overflow-y-auto pos-scroll">
          {attractions.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                onSelect(a);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-sm hover:bg-secondary"
            >
              <span className="truncate text-ink-900">{a.name}</span>
              <MoneyValue value={a.price} className="text-xs text-ink-400" />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Per main-ticket card: configures each visitor's slot, name, and attraction
 * children (add-ons). Attractions are the global `attraction`-category pool.
 */
function LineDetailCard({
  line,
  attractions,
  productById,
  resolvedPrices,
}: {
  line: CartLine;
  attractions: Product[];
  /** Every catalog product by id — resolves the cap for the main seat + children. */
  productById: Map<string, Product>;
  resolvedPrices: Record<string, number>;
}) {
  const visitDate = useCartStore((s) => s.visitDate);
  const attractionMap = useMemo(
    () => new Map(attractions.map((a) => [a.id, a])),
    [attractions],
  );
  // The main ticket's own cap (main-category tickets are uncapped by the
  // backend → -1 → inert, but resolve it faithfully for the per-attraction gate).
  const mainProduct = productById.get(line.productId);
  const mainCap = capOfProduct(mainProduct);
  const { setSeatSlot, setSeatLabel, setSeatAddOns, setSeatItinerary, applySeatToAll } = useCartStore(
    useShallow((s) => ({
      setSeatSlot: s.setSeatSlot,
      setSeatLabel: s.setSeatLabel,
      setSeatAddOns: s.setSeatAddOns,
      setSeatItinerary: s.setSeatItinerary,
      applySeatToAll: s.applySeatToAll,
    })),
  );
  // Which seat's Smart Itinerary dialog is open (null = closed).
  const [itinerarySeat, setItinerarySeat] = useState<number | null>(null);

  const slotQuery = useAvailability(line.productId, visitDate, line.requiresSlot);
  const slots = useMemo(() => selectableSlots(slotQuery.data?.slots ?? []), [slotQuery.data]);
  const qty = line.seats.length;

  // Only one slot available → auto-select it for every seat that has none.
  useEffect(() => {
    if (!line.requiresSlot || slots.length !== 1) return;
    line.seats.forEach((seat, i) => {
      if (!seat.slotId) setSeatSlot(line.key, i, slots[0]);
    });
  }, [slots, line.requiresSlot, line.seats, line.key, setSeatSlot]);

  // Each attraction ticket is a distinct entry (quantity 1) with its own slot;
  // adding the same attraction twice yields two independently-scheduled rows.
  const addChild = (seatIndex: number, p: Product) => {
    const seat = line.seats[seatIndex];
    if (!seat) return;
    setSeatAddOns(line.key, seatIndex, [
      ...seat.addOns,
      { productId: p.id, name: p.name, price: resolvedPrices[p.id] ?? p.price, quantity: 1 },
    ]);
  };
  const removeChild = (seatIndex: number, addOnIndex: number) => {
    const seat = line.seats[seatIndex];
    if (!seat) return;
    setSeatAddOns(
      line.key,
      seatIndex,
      seat.addOns.filter((_, j) => j !== addOnIndex),
    );
  };
  const setChildSlot = (seatIndex: number, addOnIndex: number, slot: Slot | undefined) => {
    const seat = line.seats[seatIndex];
    if (!seat) return;
    setSeatAddOns(
      line.key,
      seatIndex,
      seat.addOns.map((a, j) => (j === addOnIndex ? { ...a, slotId: slot?.id } : a)),
    );
  };

  // A seat's wahana that resolve to an attractionId — the only ones the backend
  // can schedule. Order preserved (visit order feeds the generate payload).
  const schedulableItemsFor = (seat: CartLine['seats'][number]): SchedulableItem[] =>
    seat.addOns.flatMap((a) => {
      const attractionId = productById.get(a.productId)?.attractionId;
      return attractionId
        ? [{ productId: a.productId, name: a.name, quantity: a.quantity, attractionId }]
        : [];
    });

  // The seat's own entry slot start ('HH:mm') — the dialog's default start time.
  const entryStartFor = (seat: CartLine['seats'][number]): string | undefined => {
    if (!seat.slotId) return undefined;
    const slot = slots.find((s) => s.id === seat.slotId);
    if (!slot) return undefined;
    const start = formatTime(slot.slotStartAt ?? slot.localStart);
    return start === '—' ? undefined : start;
  };

  // Apply a generated plan: map each SCHEDULED attraction's backend `slotId`
  // back onto the seat's add-ons (in order, one slot per unit), then persist the
  // plan + config. The backend is cap-aware, so the assigned slots are safe; the
  // existing per-slot cap gate + findCapViolations backstop still guard checkout.
  const applyItinerary = (seatIndex: number, plan: ItineraryPlan, config: ItineraryConfig) => {
    const seat = line.seats[seatIndex];
    if (!seat) return;
    // attractionId → ordered list of assigned slotIds (scheduled rows only).
    const slotsByAttraction = new Map<string, string[]>();
    for (const row of plan.items ?? []) {
      if (row?.type === 'ATTRACTION' && row.scheduled && row.slotId && row.attractionId != null) {
        const aid = String(row.attractionId);
        const queue = slotsByAttraction.get(aid) ?? [];
        queue.push(String(row.slotId));
        slotsByAttraction.set(aid, queue);
      }
    }
    // Shift the next assigned slot off each add-on's attraction queue; leave
    // unscheduled ones (no attraction or empty queue) unchanged.
    const newAddOns = seat.addOns.map((a) => {
      const attractionId = productById.get(a.productId)?.attractionId;
      if (!attractionId) return a;
      const queue = slotsByAttraction.get(String(attractionId));
      const slotId = queue?.shift();
      return slotId ? { ...a, slotId } : a;
    });
    setSeatAddOns(line.key, seatIndex, newAddOns);
    setSeatItinerary(line.key, seatIndex, {
      configured: true,
      savedAt: new Date().toISOString(),
      config,
      plan,
    });
    toast.success('Jadwal diterapkan');
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Ticket className="h-4 w-4 text-primary" /> {line.name}
          <span className="tnum rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-ink-600">
            {qty} tiket
          </span>
        </CardTitle>
        {qty > 1 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-primary"
            onClick={() => applySeatToAll(line.key, 0)}
          >
            Terapkan tiket #1 ke semua
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {line.seats.map((seat, i) => {
          // One seat = one prospective wristband. Its cap cart folds the main
          // ticket's own unit plus every attraction child on this seat.
          const seatCart = buildWristbandCart(
            [{ productId: line.productId, slotId: seat.slotId }, ...seat.addOns],
            productById,
          );
          const mainBlocked = isCapped(mainCap)
            ? capReachedSlotIds({
                cap: mainCap,
                attractionId: mainProduct?.attractionId,
                slots,
                cart: seatCart,
                held: null,
              })
            : null;
          return (
          <div
            key={i}
            className="grid grid-cols-1 gap-3 rounded-lg border-2 border-border bg-cream-50/40 p-3 sm:grid-cols-[auto_1fr]"
          >
            <span className="flex items-center gap-1.5 text-sm font-semibold text-ink-900 sm:w-24">
              Tiket #{i + 1}
            </span>
            <div className="grid gap-2.5">
              {line.requiresSlot && (
                <div className="space-y-1">
                  <Label className="flex items-center gap-1.5 text-xs">
                    <Clock className="h-3.5 w-3.5 text-gold-600" /> Waktu kunjungan
                  </Label>
                  {slotQuery.isLoading ? (
                    <Skeleton className="h-9 w-full" />
                  ) : slotQuery.isError ? (
                    <button
                      type="button"
                      onClick={() => slotQuery.refetch()}
                      className="text-xs font-medium text-destructive underline"
                    >
                      Gagal memuat slot — coba lagi
                    </button>
                  ) : slots.length === 0 ? (
                    <p className="text-xs text-warning">Tidak ada slot untuk tanggal ini.</p>
                  ) : (
                    <Select
                      value={seat.slotId ?? SLOT_NONE}
                      onValueChange={(v) =>
                        setSeatSlot(line.key, i, v === SLOT_NONE ? undefined : slots.find((s) => s.id === v))
                      }
                    >
                      <SelectTrigger aria-label={`Waktu tiket ${i + 1}`}>
                        <SelectValue placeholder="Pilih waktu" />
                      </SelectTrigger>
                      <SelectContent>
                        {slots.map((s) => {
                          const capBlocked = !!mainBlocked?.has(s.id) && s.id !== seat.slotId;
                          return (
                            <SelectItem key={s.id} value={s.id} disabled={capBlocked}>
                              {slotTimeLabel(s)}
                              {capBlocked ? ` · ${CAP_REACHED_LABEL}` : ` · Sisa ${s.availableCount}`}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                  {line.requiresSlot && !seat.slotId && slots.length > 0 && !slotQuery.isLoading && (
                    <p className="inline-flex items-center gap-1 text-xs text-warning">
                      <AlertCircle className="h-3 w-3" /> Wajib dipilih
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1">
                <Label htmlFor={`name-${line.key}-${i}`} className="text-xs">
                  Nama pengunjung (opsional)
                </Label>
                <Input
                  id={`name-${line.key}-${i}`}
                  placeholder={`Tiket #${i + 1}`}
                  maxLength={15}
                  value={seat.label ?? ''}
                  onChange={(e) =>
                    // BE unitLabel: 1..15 letters/numbers/spaces/hyphens.
                    setSeatLabel(line.key, i, e.target.value.replace(/[^\p{L}\p{N} -]/gu, ''))
                  }
                />
              </div>

              {attractions.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5 text-xs">
                    <FerrisWheel className="h-3.5 w-3.5 text-gold-600" /> Wahana pengunjung ini
                  </Label>
                  {seat.addOns.map((child, ai) => (
                    <AttractionChildRow
                      key={ai}
                      child={child}
                      attraction={attractionMap.get(child.productId)}
                      visitDate={visitDate}
                      cart={seatCart}
                      onSlot={(slot) => setChildSlot(i, ai, slot)}
                      onRemove={() => removeChild(i, ai)}
                    />
                  ))}
                  <AddAttractionPopover attractions={attractions} onSelect={(a) => addChild(i, a)} />
                  {schedulableItemsFor(seat).length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-full gap-1.5 border-gold-500/50 text-primary hover:border-gold-500 hover:bg-gold-200/30"
                      onClick={() => setItinerarySeat(i)}
                    >
                      <Wand2 className="h-3.5 w-3.5" /> Atur Jadwal Pintar
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
          );
        })}

        {itinerarySeat != null && line.seats[itinerarySeat] && (
          <SmartItineraryDialog
            open
            onOpenChange={(o) => {
              if (!o) setItinerarySeat(null);
            }}
            ticketName={line.name}
            displayNumber={itinerarySeat + 1}
            schedulableItems={schedulableItemsFor(line.seats[itinerarySeat])}
            visitDate={visitDate}
            entrySlotStart={entryStartFor(line.seats[itinerarySeat])}
            onApply={(plan, config) => applyItinerary(itinerarySeat, plan, config)}
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One slot-required bundle component — its own time-slot picker. Mirrors
 * AttractionChildRow: the backend needs a slot for every requiresTimeSlot
 * component (sent as componentSlots), so an unpicked one blocks checkout.
 */
function BundleComponentRow({
  component,
  product,
  visitDate,
  cart,
  onSlot,
}: {
  component: { productId: string; name: string; slotId?: string };
  /** Catalog product for this component — carries the attraction cap. */
  product?: Product;
  visitDate: string;
  /** Per-(attraction, slot) counts for the bundle's wristband — cap gate input. */
  cart: Map<string, number>;
  onSlot: (slot: Slot | undefined) => void;
}) {
  const slotQuery = useAvailability(component.productId, visitDate);
  const slots = useMemo(() => selectableSlots(slotQuery.data?.slots ?? []), [slotQuery.data]);
  const missing = !component.slotId;

  const cap = capOfProduct(product);
  const blockedSlotIds = isCapped(cap)
    ? capReachedSlotIds({ cap, attractionId: product?.attractionId, slots, cart, held: null })
    : null;

  // Single slot → auto-select it (no reason to make the cashier pick).
  useEffect(() => {
    if (slots.length === 1 && !component.slotId) onSlot(slots[0]);
  }, [slots, component.slotId, onSlot]);

  return (
    <div className="rounded-md border-2 border-border bg-card p-2.5">
      <div className="flex min-w-0 items-center gap-1.5">
        <FerrisWheel className="h-3.5 w-3.5 shrink-0 text-gold-600" />
        <span className="truncate text-sm font-medium text-ink-900">{component.name}</span>
      </div>
      <div className="mt-2">
        {slotQuery.isLoading ? (
          <Skeleton className="h-8 w-full" />
        ) : slots.length === 0 ? (
          <p className="text-xs text-warning">Tidak ada slot untuk tanggal ini.</p>
        ) : (
          <Select
            value={component.slotId ?? SLOT_NONE}
            onValueChange={(v) =>
              onSlot(v === SLOT_NONE ? undefined : slots.find((s) => s.id === v))
            }
          >
            <SelectTrigger className="h-8" aria-label={`Waktu ${component.name}`}>
              <SelectValue placeholder="Pilih waktu" />
            </SelectTrigger>
            <SelectContent>
              {slots.map((s) => {
                const capBlocked = !!blockedSlotIds?.has(s.id) && s.id !== component.slotId;
                return (
                  <SelectItem key={s.id} value={s.id} disabled={capBlocked}>
                    {slotTimeLabel(s)}
                    {capBlocked ? ` · ${CAP_REACHED_LABEL}` : ` · Sisa ${s.availableCount}`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
        {missing && slots.length > 0 && (
          <p className="mt-1 inline-flex items-center gap-1 text-xs text-warning">
            <AlertCircle className="h-3 w-3" /> Pilih waktu
          </p>
        )}
      </div>
    </div>
  );
}

export function DetailsPage() {
  const navigate = useNavigate();
  const { lines, bundleLines } = useCartStore(
    useShallow((s) => ({ lines: s.lines, bundleLines: s.bundleLines })),
  );
  const setBundleComponentSlot = useCartStore((s) => s.setBundleComponentSlot);
  const setBundleAddOns = useCartStore((s) => s.setBundleAddOns);
  const visitDate = useCartStore((s) => s.visitDate);
  // Keeps prices date-resolved as attractions are added here (and returns the
  // product-price map so new attractions are added at the resolved price).
  const { productPrices } = usePriceSync();
  const subtotal = useCartStore(cartSelectors.subtotal);
  const total = useCartStore(selectTotal);
  const cartCount = lines.length + bundleLines.length;

  const { data: productData } = useProducts();
  // Global attraction (child) pool — any attraction attaches to any main ticket.
  const attractions = useMemo(
    () => (productData?.items ?? []).filter(isAttraction),
    [productData],
  );
  const attractionNeedsSlot = useMemo(
    () => new Set(attractions.filter((a) => a.requiresSlot).map((a) => a.id)),
    [attractions],
  );
  const attractionMap = useMemo(() => new Map(attractions.map((a) => [a.id, a])), [attractions]);
  // Every catalog product by id — resolves the purchase cap for the main seat,
  // attraction children, and bundle components (their attraction may not be in
  // the child-only `attractions` pool).
  const productById = useMemo(
    () => new Map((productData?.items ?? []).map((p) => [p.id, p])),
    [productData],
  );

  // Ready when every slot-required MAIN seat AND every slot-required attraction
  // child has a time picked. Derived locally (a store selector returning a
  // fresh array would break useSyncExternalStore with an infinite render loop).
  const canProceed = useMemo(
    () =>
      lines.every(
        (l) =>
          (!l.requiresSlot || l.seats.every((seat) => seat.slotId)) &&
          l.seats.every((seat) =>
            seat.addOns.every((a) => !attractionNeedsSlot.has(a.productId) || a.slotId),
          ),
      ) &&
      // Every slot-required bundle component AND every slot-required extra
      // attraction must have a time picked too.
      bundleLines.every(
        (b) =>
          b.components.every((c) => c.slotId) &&
          b.extraAddOns.every((a) => !attractionNeedsSlot.has(a.productId) || a.slotId),
      ),
    [lines, attractionNeedsSlot, bundleLines],
  );

  // Purchase-cap backstop: catch any per-wristband breach the per-slot option
  // disabling can't pre-empt (e.g. "apply #1 to all" fanning add-ons). Each seat
  // and each bundle is its own wristband — the cap is per-wristband, so check
  // them independently rather than tallying the whole cart. UX only; the /sale
  // path re-checks and 409s regardless.
  const hasCapViolation = useMemo(() => {
    for (const l of lines) {
      for (const seat of l.seats) {
        if (
          wristbandHasCapViolation(
            [{ productId: l.productId, slotId: seat.slotId }, ...seat.addOns],
            productById,
          )
        ) {
          return true;
        }
      }
    }
    for (const b of bundleLines) {
      if (wristbandHasCapViolation([...b.components, ...b.extraAddOns], productById)) return true;
    }
    return false;
  }, [lines, bundleLines, productById]);

  const proceed = () => {
    if (hasCapViolation) {
      toast.warning(`${CAP_REACHED_LABEL} untuk sebagian jam. Kurangi jumlah atau ganti jam.`);
      return;
    }
    navigate('/checkout');
  };

  // Aggregate attraction children across the whole cart for the summary,
  // itemized separately from the main tickets (which show main-only price).
  const attractionSummary = useMemo(() => {
    const map = new Map<string, { name: string; quantity: number; total: number }>();
    const fold = (a: CartLineAddOn) => {
      const cur = map.get(a.productId) ?? { name: a.name, quantity: 0, total: 0 };
      cur.quantity += a.quantity;
      cur.total += a.price * a.quantity;
      map.set(a.productId, cur);
    };
    for (const l of lines) for (const seat of l.seats) seat.addOns.forEach(fold);
    // Extra attractions attached to bundles are itemized here too.
    for (const b of bundleLines) b.extraAddOns.forEach(fold);
    return Array.from(map.values());
  }, [lines, bundleLines]);

  // Empty-cart guard.
  useEffect(() => {
    if (cartCount === 0) navigate('/', { replace: true });
  }, [cartCount, navigate]);

  useHotkeys({
    f8: () => canProceed && proceed(),
    escape: () => navigate('/'),
  });

  if (cartCount === 0) return null;

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col px-6 py-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <Button variant="ghost" className="gap-2" onClick={() => navigate('/')}>
          <ArrowLeft className="h-4 w-4" /> Kembali ke katalog
        </Button>
        <StepIndicator current={1} />
      </div>

      <div className="grid min-h-0 flex-1 gap-6 lg:grid-cols-5">
        {/* Left — per-ticket configuration. `min-h-0` lets this grid item shrink
            below its content so the inner ScrollArea gets a bounded height. */}
        <div className="min-h-0 lg:col-span-3">
          <ScrollArea className="h-full pr-3">
            <div className="space-y-5 pb-6">
              {lines.map((line) => (
                <LineDetailCard
                  key={line.key}
                  line={line}
                  attractions={attractions}
                  productById={productById}
                  resolvedPrices={productPrices}
                />
              ))}

              {bundleLines.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Package className="h-4 w-4 text-primary" /> Paket
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {bundleLines.map((b) => {
                      // One bundle = one prospective wristband; its components and
                      // extra attractions share the same per-slot quota.
                      const bundleCart = buildWristbandCart(
                        [...b.components, ...b.extraAddOns],
                        productById,
                      );
                      return (
                      <div key={b.key} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-ink-900">
                            {b.name} <span className="text-ink-400">× {b.quantity}</span>
                          </span>
                          <MoneyValue value={b.unitPrice * b.quantity} className="text-sm" />
                        </div>
                        {b.components.length > 0 ? (
                          <div className="space-y-2">
                            <p className="flex items-center gap-1 text-xs font-medium text-ink-600">
                              <Clock className="h-3 w-3" /> Pilih waktu tiap wahana
                            </p>
                            {b.components.map((c) => (
                              <BundleComponentRow
                                key={c.productId}
                                component={c}
                                product={productById.get(c.productId)}
                                visitDate={visitDate}
                                cart={bundleCart}
                                onSlot={(slot) => setBundleComponentSlot(b.key, c.productId, slot)}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-ink-400">
                            Komponen paket diatur otomatis oleh sistem.
                          </p>
                        )}

                        {/* Extra attractions on top of the bundle (charged separately). */}
                        {attractions.length > 0 && (
                          <div className="space-y-2 border-t border-dashed border-border pt-3">
                            <p className="flex items-center gap-1 text-xs font-medium text-ink-600">
                              <FerrisWheel className="h-3 w-3 text-gold-600" /> Wahana tambahan
                            </p>
                            {b.extraAddOns.map((child, ai) => (
                              <AttractionChildRow
                                key={ai}
                                child={child}
                                attraction={attractionMap.get(child.productId)}
                                visitDate={visitDate}
                                cart={bundleCart}
                                onSlot={(slot) =>
                                  setBundleAddOns(
                                    b.key,
                                    b.extraAddOns.map((a, j) =>
                                      j === ai ? { ...a, slotId: slot?.id } : a,
                                    ),
                                  )
                                }
                                onRemove={() =>
                                  setBundleAddOns(
                                    b.key,
                                    b.extraAddOns.filter((_, j) => j !== ai),
                                  )
                                }
                              />
                            ))}
                            <AddAttractionPopover
                              attractions={attractions}
                              onSelect={(p) =>
                                setBundleAddOns(b.key, [
                                  ...b.extraAddOns,
                                  {
                                    productId: p.id,
                                    name: p.name,
                                    price: productPrices[p.id] ?? p.price,
                                    quantity: 1,
                                  },
                                ])
                              }
                            />
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Right — summary */}
        <div className="min-h-0 lg:col-span-2">
          <Card className="flex h-full flex-col">
            <CardHeader>
              <CardTitle>Ringkasan</CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col">
              <ScrollArea className="flex-1">
                <ul className="space-y-2 pr-2">
                  {bundleLines.map((b) => (
                    <li key={b.key} className="flex justify-between gap-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink-900">📦 {b.name}</span>
                        <span className="tnum text-xs text-ink-400">paket · × {b.quantity}</span>
                      </span>
                      <MoneyValue value={b.unitPrice * b.quantity} className="text-sm" />
                    </li>
                  ))}
                  {lines.map((l) => (
                    <li key={l.key} className="flex justify-between gap-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink-900">{l.name}</span>
                        <span className="tnum text-xs text-ink-400">× {l.seats.length}</span>
                      </span>
                      <MoneyValue value={l.unitPrice * l.seats.length} className="text-sm" />
                    </li>
                  ))}
                  {attractionSummary.map((a) => (
                    <li key={a.name} className="flex justify-between gap-2 text-sm">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <FerrisWheel className="h-3.5 w-3.5 shrink-0 text-gold-600" />
                        <span className="min-w-0">
                          <span className="block truncate text-ink-700">{a.name}</span>
                          <span className="tnum text-xs text-ink-400">× {a.quantity}</span>
                        </span>
                      </span>
                      <MoneyValue value={a.total} className="text-sm" />
                    </li>
                  ))}
                </ul>
              </ScrollArea>

              <div className="mt-4 space-y-1.5 border-t border-border pt-4 text-sm">
                <div className="flex justify-between text-ink-600">
                  <span>Subtotal</span>
                  <MoneyValue value={subtotal} />
                </div>
                <Separator className="my-2" />
                <div className="flex items-end justify-between">
                  <span className="font-semibold uppercase tracking-wide text-ink-600">Total</span>
                  <MoneyValue value={total} emphasis className="text-3xl" />
                </div>
              </div>

              <Button
                size="lg"
                variant="gold"
                className="mt-4 h-16 w-full text-xl"
                disabled={!canProceed}
                onClick={proceed}
              >
                Lanjut ke Pembayaran <ArrowRight className="h-6 w-6" />
                <kbd className="ml-1 rounded border border-ink-900/20 px-1.5 text-xs">F8</kbd>
              </Button>
              {!canProceed && (
                <p className="mt-2 text-center text-xs text-warning">
                  Pilih waktu untuk semua tiket yang membutuhkannya.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
